import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { Gift, Check, Loader2, Sparkles, FileVideo, FileText, ListChecks, Library, ShieldCheck } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { useAuth } from '../App';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ax = { withCredentials: true };

export default function StudioInvite() {
  const { token } = useParams();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();

  const [invite, setInvite] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [claimState, setClaimState] = useState('idle'); // idle | polling | done

  const params = new URLSearchParams(location.search);
  const sessionId = params.get('session_id');
  const canceled = params.get('canceled');

  const loadInvite = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/studio-invite/${token}`, ax);
      setInvite(r.data);
    } catch (e) {
      setLoadErr(e.response?.data?.detail || 'This invite is no longer available.');
    }
  }, [token]);

  useEffect(() => { loadInvite(); }, [loadInvite]);

  // Poll for fulfillment after returning from Stripe
  const pollStatus = useCallback(async (attempt = 0) => {
    if (attempt > 12) { setClaimState('idle'); setError('Still processing your payment — refresh in a moment.'); return; }
    try {
      const r = await axios.get(`${API_URL}/api/studio-invite/${token}/status`, ax);
      if (r.data?.access_active || r.data?.status === 'imported') {
        setClaimState('done');
        return;
      }
    } catch { /* keep polling */ }
    setTimeout(() => pollStatus(attempt + 1), 2500);
  }, [token]);

  useEffect(() => {
    if (sessionId && user) { setClaimState('polling'); pollStatus(0); }
  }, [sessionId, user, pollStatus]);

  const onClaim = async () => {
    setError('');
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/studio-invite/${token}/checkout`,
        { origin_url: window.location.origin }, ax);
      if (r.data?.already_active) { setClaimState('done'); return; }
      if (r.data?.url) { window.location.href = r.data.url; return; }
      setError('Could not start checkout. Please try again.');
    } catch (e) {
      setError(e.response?.data?.detail || 'Checkout failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (loadErr) {
    return (
      <Layout>
        <section className="section-padding bg-[#FAFAFA]">
          <div className="container-custom max-w-lg text-center py-16" data-testid="studio-invite-error">
            <h1 className="heading-1 text-2xl text-[#0A0F1C] mb-3">Invite unavailable</h1>
            <p className="text-[#475569]">{loadErr}</p>
          </div>
        </section>
      </Layout>
    );
  }

  if (!invite) {
    return (
      <Layout>
        <div className="min-h-[50vh] flex items-center justify-center">
          <Loader2 className="animate-spin text-[#1D4ED8]" size={28} aria-hidden="true" />
        </div>
      </Layout>
    );
  }

  const s = invite.summary || {};
  const price = invite.price_usd ?? 10;
  const alreadyClaimed = invite.status === 'imported' || claimState === 'done';

  const features = [
    { icon: FileVideo, label: `${s.items || 0} ready-to-use posts & video scripts` },
    { icon: ListChecks, label: `${s.days || 0}-day content calendar` },
    { icon: Sparkles, label: `Tuned theme bank (${s.themes_count || 0} topics)` },
    ...(s.references_count ? [{ icon: Library, label: `${s.references_count} reference document${s.references_count > 1 ? 's' : ''}` }] : []),
  ];

  return (
    <Layout>
      <section className="section-padding bg-[#FAFAFA]" data-testid="studio-invite-page">
        <div className="container-custom max-w-4xl">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-[#EFF6FF] text-[#1D4ED8] px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
              <Gift size={14} aria-hidden="true" /> A gift to jumpstart you
            </div>
            <h1 className="heading-1 text-3xl lg:text-5xl text-[#0A0F1C] mb-4">
              {invite.sender_name} shared a content plan with you
            </h1>
            <p className="body-text text-[#475569] max-w-2xl mx-auto">
              Claim a ready-to-use <strong>{s.days}-day plan</strong> for <strong>{s.brand_name}</strong> —
              it loads straight into your own Social Studio so you never start from a blank page.
            </p>
          </div>

          {canceled && claimState !== 'done' && (
            <div className="max-w-xl mx-auto mb-6 bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-3 rounded-lg text-center" data-testid="studio-invite-canceled">
              Checkout was canceled. You can claim your plan whenever you're ready.
            </div>
          )}

          {alreadyClaimed ? (
            <div className="max-w-xl mx-auto bg-white border border-emerald-200 rounded-2xl p-8 text-center shadow-sm" data-testid="studio-invite-done">
              <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check size={28} className="text-emerald-600" aria-hidden="true" />
              </div>
              <h2 className="text-xl font-bold text-[#0A0F1C] mb-2">You're all set!</h2>
              <p className="text-[#475569] text-sm mb-6">
                Your content plan, brand profile and theme bank are now in your account.
                It's fully yours to edit and regenerate.
              </p>
              <Link to="/admin/social-studio" className="inline-flex items-center gap-2 bg-[#F97316] hover:bg-[#FB923C] text-white px-6 py-3 rounded-md font-bold text-sm transition-colors" data-testid="studio-invite-open-studio">
                Open my Social Studio →
              </Link>
            </div>
          ) : (
            <div className="grid lg:grid-cols-2 gap-8 items-start">
              {/* What's included */}
              <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 lg:p-8 shadow-sm">
                <h2 className="text-lg font-bold text-[#0A0F1C] mb-1">What's included</h2>
                <p className="text-sm text-[#94A3B8] mb-5">Your independent copy — your edits never affect the sender's.</p>
                <ul className="space-y-3" data-testid="studio-invite-features">
                  {features.map((f, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-[#0A0F1C]">
                      <span className="w-8 h-8 bg-[#EFF6FF] rounded-lg flex items-center justify-center flex-shrink-0">
                        <f.icon size={16} className="text-[#1D4ED8]" aria-hidden="true" />
                      </span>
                      <span className="mt-1.5">{f.label}</span>
                    </li>
                  ))}
                </ul>
                {invite.message && (
                  <div className="mt-6 border-l-4 border-[#1D4ED8] bg-[#F8FAFC] px-4 py-3 rounded-r-lg">
                    <p className="text-xs font-bold uppercase tracking-wider text-[#94A3B8] mb-1">Note from {invite.sender_name}</p>
                    <p className="text-sm text-[#334155] italic">"{invite.message}"</p>
                  </div>
                )}
              </div>

              {/* Claim / paywall */}
              <div className="bg-[#0F172A] text-white rounded-2xl p-6 lg:p-8 shadow-md">
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-4xl font-extrabold">${price.toFixed(2)}</span>
                  <span className="text-sm font-bold text-white/60">/month</span>
                </div>
                <p className="text-sm text-white/70 mb-6">Full Social Studio access. Cancel anytime.</p>

                {claimState === 'polling' ? (
                  <div className="bg-white/10 rounded-lg p-4 text-center text-sm" data-testid="studio-invite-processing">
                    <Loader2 size={20} className="animate-spin mx-auto mb-2" aria-hidden="true" />
                    Confirming your payment and loading your plan…
                  </div>
                ) : authLoading ? (
                  <div className="text-center py-4"><Loader2 size={20} className="animate-spin mx-auto" aria-hidden="true" /></div>
                ) : user ? (
                  <>
                    {error && <div className="bg-red-500/20 border border-red-500/40 text-red-200 text-sm px-3 py-2 rounded-md mb-3" data-testid="studio-invite-claim-error">{error}</div>}
                    <button onClick={onClaim} disabled={busy}
                      className="w-full bg-[#F97316] hover:bg-[#FB923C] text-white px-6 py-3 rounded-md font-bold text-sm transition-colors inline-flex items-center justify-center gap-2"
                      data-testid="studio-invite-claim-btn">
                      {busy ? <><Loader2 size={16} className="animate-spin" aria-hidden="true" /> Starting…</> : <>Subscribe ${price.toFixed(2)}/mo &amp; claim my plan</>}
                    </button>
                    <p className="text-[11px] text-white/50 mt-3 text-center inline-flex items-center justify-center gap-1 w-full">
                      <ShieldCheck size={12} aria-hidden="true" /> Secure Stripe checkout · signed in as {user.email}
                    </p>
                  </>
                ) : (
                  <div className="space-y-3" data-testid="studio-invite-auth-gate">
                    <p className="text-sm text-white/80 mb-1">Create your free account (or log in) to claim it:</p>
                    <Link to="/register" state={{ from: { pathname: `/studio-invite/${token}` } }}
                      className="w-full block text-center bg-[#F97316] hover:bg-[#FB923C] text-white px-6 py-3 rounded-md font-bold text-sm transition-colors"
                      data-testid="studio-invite-signup-btn">
                      Create my account
                    </Link>
                    <Link to="/login" state={{ from: { pathname: `/studio-invite/${token}` } }}
                      className="w-full block text-center bg-white/10 hover:bg-white/15 text-white px-6 py-3 rounded-md font-bold text-sm transition-colors"
                      data-testid="studio-invite-login-btn">
                      I already have an account
                    </Link>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
